import styles from "./ScrollChat.module.css";
// import "./ScrollChatModule.css";
import { commonIcon } from "../../../asset";
import { useSelector, useDispatch } from "react-redux";
import React, { useRef, useEffect, Fragment, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChat } from "../../../store/chat-action";
import ReplyByModel from "./ReplyByModel";
import ResponsePromptSection from "../ResponsePrompt/ResponsePrompt";
import CopyBtn from "../../Ui/CopyBtn";
import ShareBtn from "../../Ui/ShareBtn";
import FeedbackBtn from "../../Ui/FeedbackBtn";
import ExportToDocBtn from "../../Ui/ExportToDocBtn";
import ExportSourceToDoc from "../../Ui/ExportSourceToDoc";
import { ROLE } from "../../../constants/index";

import TextToSpeech from "../../Ui/TextToSpeech";
import CustomTextToSpeech from "../../Ui/TextToSpeechCustom";
import DownloadAudio from "../../Ui/DownloadAudio";
import MixingAudio from "../../Ui/MixingAudio";
import useBodyTourStep from "../../../hooks/use-body-tour-step";

const ScrollChat = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { historyId } = useParams();
  const chatRef = useRef(null);
  const messages = useSelector((state) => state.chat.messages);
  const chatHistoryId = useSelector((state) => state.chat.chatHistoryId);
  const isLoader = useSelector((state) => state.chat.isLoader);
  const [scrollH, setScrollH] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [userScrollTimeout, setUserScrollTimeout] = useState(null);
  
  // Auto-scroll function with smooth "vèo" effect to bottom
  const scrollToBottom = () => {
    if (chatRef.current && !isUserScrolling) {
      const scrollElement = chatRef.current;
      
      // Use requestAnimationFrame for smoother scrolling with gentle easing
      requestAnimationFrame(() => {
        scrollElement.scrollTo({
          top: scrollElement.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  };

  // Handle user scroll detection
  const handleScroll = () => {
    if (chatRef.current) {
      const scrollElement = chatRef.current;
      const isAtBottom = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;
      
      if (!isAtBottom) {
        // User is scrolling up, pause auto-scroll
        setIsUserScrolling(true);
        
        // Clear existing timeout
        if (userScrollTimeout) {
          clearTimeout(userScrollTimeout);
        }
        
        // Set timeout to resume auto-scroll if user stops scrolling
        const timeout = setTimeout(() => {
          setIsUserScrolling(false);
        }, 2000); // Resume auto-scroll after 2 seconds of no scrolling
        
        setUserScrollTimeout(timeout);
      } else {
        // User is at bottom, resume auto-scroll
        setIsUserScrolling(false);
        if (userScrollTimeout) {
          clearTimeout(userScrollTimeout);
          setUserScrollTimeout(null);
        }
      }
    }
  };

  // Alternative scroll function for more reliable scrolling
  const scrollToBottomInstant = () => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  };
  const isReplying = useSelector((state) => state.chat.isReplying);
  const userDetails = useSelector((state) => state.user.user);
  const isLogin = useSelector((state) => state.user.isLogin);

  useEffect(() => {
    if(!userDetails.id){
      return;
    }

    if (messages.length === 0 && !historyId ) {
      navigate("/home");
    } else if (historyId && historyId !== chatHistoryId) {
      dispatch(getChat(historyId));
    } else {
      navigate(`/home/app/${chatHistoryId}`);
    }
  }, [dispatch, historyId, chatHistoryId, navigate, messages, isLogin]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    if (messages.length > 0) {
      // Always scroll when messages change, regardless of isReplying state
      setTimeout(() => {
        scrollToBottom();
      }, 200); // Slower response for smoother experience
    }
  }, [messages.length]);

  // Additional scroll during text generation
  useEffect(() => {
    if (isReplying && messages.length > 0) {
      // Scroll during text generation with smoother timing
      const interval = setInterval(() => {
        scrollToBottom(); // Use smooth scroll for better experience
      }, 400); // Scroll every 400ms for smoother experience
      
      return () => clearInterval(interval);
    }
  }, [isReplying, messages.length, isUserScrolling]);

  // Add scroll event listener
  useEffect(() => {
    const scrollElement = chatRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        if (userScrollTimeout) {
          clearTimeout(userScrollTimeout);
        }
      };
    }
  }, [userScrollTimeout]);

  const mockMessage = [
    {
      _id: "g1",
      role: "assistant",
      newChat: true,
      message: {
        user: "Hello, how are you?",
        gemini: "I'm good, thank you! How can I help you today?"
      }
    }
  ]

  const step = useBodyTourStep()

  const isTour = step === 4 || step === 5;

  const chatSection = (isTour ? mockMessage : messages)?.map((c, index) => (
    <Fragment key={c?._id + "" + index}>
      <div
        className={`${styles["single-chat"]} ${
          index === messages.length - 1 ? styles["last-single-chat"] : ""
        }`}
      >
        <div className="flex w-full flex-col gap-1 empty:hidden items-end rtl:items-start">
          <div className="text-right relative max-w-[70%] rounded-3xl bg-[#f4f4f4] px-5 py-2.5 dark:bg-token-main-surface-secondary">
            <p>{c.message?.user}</p>
          </div>
        </div>

        <div className={styles["gemini"]}>
          <img
            src={commonIcon.chatGeminiIcon}
            alt="avater icon"
            className={`border border-slate-200 rounded-full h-8 w-8 p-[4px] ${
              isLoader && !c.message?.gemini && index === messages.length - 1
                ? "animate-spin-slow"
                : ""
            }`}
          ></img>

          <ReplyByModel
            gemini={c.message?.gemini}
            isNewChat={c.newChat}
            scrollHeight={scrollToBottom}
            scrollHeightInstant={scrollToBottomInstant}
            isUserScrolling={isUserScrolling}
          />
        </div>
        {(!isReplying || isTour) && (
          <>
            <div className="flex items-center relative">
              <div data-tour="player-icons" className="absolute right-0 flex justify-center items-center gap-2">
                
                {c?.message?.gemini?.length > 0 && c.role !== "user" && userDetails.id && (
                  <FeedbackBtn
                    messageId={c._id}
                    chatHistoryId={chatHistoryId}
                    question={c.message?.user}
                    aiResponse={c.message?.gemini}
                  />
                )}
                {userDetails?.role === ROLE.ADMIN && userDetails.id && (
                  <MixingAudio messageId={c._id} />
                )}
                {userDetails.id && (
                <CustomTextToSpeech messageId={c._id} />
                )}
                {userDetails?.role === ROLE.ADMIN && isLogin && (
                  <DownloadAudio id={c._id} />
                )}
                {userDetails.id && (
                <TextToSpeech messageId={c._id} />
                )}
                {userDetails?.role === ROLE.ADMIN && userDetails.id && (
                  <ExportSourceToDoc
                    content={c.sources}
                    name={c.message?.user}
                  />
                )}
                {userDetails.id && ( 
                <ExportToDocBtn
                  content={c.message?.gemini}
                    name={c.message?.user}
                  />
                )}
                {userDetails.id && (
                <ShareBtn
                  messageId={c._id}
                  historyId={chatHistoryId}
                  name={c.message?.user}
                  content={c.message?.gemini}
                />
                )}
                {c?.message?.gemini?.length > 0 && c.role !== "user" && (
                  <CopyBtn data={c?.message?.gemini} />
                )}
              </div>
            </div>
            {index === messages.length - 1 &&
              c.newChat &&
              c.message?.gemini && (
                <ResponsePromptSection chatType={c.chatType} />
              )}
          </>
        )}
      </div>
    </Fragment>
  ));

  return (
    <div className={styles["scroll-chat-main"]} ref={chatRef}>
      {chatSection}
      {/* {messageSection} */}
    </div>
  );
};

export default ScrollChat;
